import { useCallback, useState } from 'react'
import { cn } from '@/lib/utils'
import { LANDSCAPES } from '@/lib/visual'
import { CategorizePreview } from '@/components/landing/ai/categorize-preview'
import { ChatPreview } from '@/components/landing/ai/chat-preview'
import { FeatureCard } from '@/components/landing/ai/feature-card'
import { ForecastPreview } from '@/components/landing/ai/forecast-preview'

export const AI_FEATURES = [
    {
        title: 'Plain-Language Answers',
        description: 'Type a question the way you would say it out loud, and get a clear reply built on your real numbers.',
        image: LANDSCAPES.pampas,
        render: (active: boolean) => <ChatPreview active={active} />
    },
    {
        title: 'Ninety-Day Forecasts',
        description: 'Look three months down the road, with quiet heads-ups well before a tight week arrives.',
        image: LANDSCAPES.hillTree,
        render: (active: boolean) => <ForecastPreview active={active} />
    },
    {
        title: 'Automatic Sorting',
        description: 'Each purchase finds its category as it clears, so your budget stays honest without the upkeep.',
        image: LANDSCAPES.wheatField,
        render: (active: boolean) => <CategorizePreview active={active} />
    }
]

interface ProgressLineProps {
    seen: Set<number>
    total: number
    className?: string
}

/** Thin segmented line that fills as each feature card comes into view. */
export const ProgressLine = ({ seen, total, className }: ProgressLineProps) => {
    return (
        <div className={cn('flex items-center justify-center gap-4', className)}>
            <span className='text-[11px] tabular-nums tracking-[0.2em] text-cream/45'>
                0{seen.size}
            </span>

            <div
                role='progressbar'
                aria-label='Features shown'
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={seen.size}
                className='flex w-40 items-center gap-1.5 sm:w-56'
            >
                {Array.from({ length: total }, (_, index) => (
                    <span key={index} className='h-[2px] flex-1 overflow-hidden rounded-full bg-white/[0.12]'>
                        <span
                            className='block h-full rounded-full bg-[linear-gradient(90deg,#e2f37a,#8fae62)] transition-[width] duration-1000 ease-cinema'
                            style={{ width: seen.has(index) ? '100%' : '0%' }}
                        />
                    </span>
                ))}
            </div>

            <span className='text-[11px] tabular-nums tracking-[0.2em] text-cream/45'>
                0{total}
            </span>
        </div>
    )
}

export const useSeenSet = () => {
    const [seen, setSeen] = useState<Set<number>>(() => new Set())

    const markSeen = useCallback((index: number) => {
        setSeen(previous => {
            if (previous.has(index)) return previous

            const next = new Set(previous)

            next.add(index)

            return next
        })
    }, [])

    return { seen, markSeen }
}

interface FeatureGridProps {
    onVisible: (index: number) => void
    className?: string
}

export const FeatureGrid = ({ onVisible, className }: FeatureGridProps) => {
    return (
        <div className={cn('grid gap-5 md:grid-cols-3 lg:gap-6', className)}>
            {AI_FEATURES.map((feature, index) => (
                <FeatureCard
                    key={feature.title}
                    index={index}
                    title={feature.title}
                    description={feature.description}
                    image={feature.image}
                    onVisible={onVisible}
                >
                    {feature.render}
                </FeatureCard>
            ))}
        </div>
    )
}
